/*
 * Thin progressive-enhancement layer. The server (Go + templ) owns all state and
 * rendering; htmx (vendored, loaded before this file) does the network swaps. This
 * file only adds the few behaviours native elements + htmx do not give us for free:
 *
 *   1. opening native <dialog> modals from declarative attributes,
 *   2. auto-dismissing server-pushed toasts, and
 *   3. toggling the color theme (persisted in a cookie so the server renders the
 *      correct theme on the next request with no flash).
 *
 * Keep it small. If something can be done with a native element or an htmx attribute,
 * do it there instead of here. No framework, no Alpine.
 */

/**
 * Initialise the browser Sentry SDK (vendored, loaded via <script> when the server
 * has a frontend DSN configured). No-op when the script or meta tags are absent.
 */
function initSentry(): void {
  if (typeof Sentry === 'undefined') return;
  const dsn = document.querySelector<HTMLMetaElement>('meta[name="sentry-dsn"]')?.content;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: document.querySelector<HTMLMetaElement>('meta[name="sentry-environment"]')
      ?.content,
    release: document.querySelector<HTMLMetaElement>('meta[name="sentry-release"]')?.content,
  });
}

initSentry();

const TOAST_TTL_MS = 4000;
const TOAST_LEAVE_MS = 200;

/** Open/close native <dialog> elements via [data-dialog-open="id"] / [data-dialog-close]. */
function setupDialogs(): void {
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const opener = target.closest<HTMLElement>('[data-dialog-open]');
    if (opener) {
      const id = opener.dataset.dialogOpen;
      const dialog = id ? document.getElementById(id) : null;
      if (dialog instanceof HTMLDialogElement) {
        dialog.showModal();
      }
      return;
    }

    const closer = target.closest<HTMLElement>('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog instanceof HTMLDialogElement) {
        dialog.close();
      }
    }
  });
}

/** Remove a toast after a delay, playing the leave transition first. */
function scheduleDismiss(toast: HTMLElement): void {
  window.setTimeout(() => {
    toast.classList.add('toast--leaving');
    window.setTimeout(() => toast.remove(), TOAST_LEAVE_MS);
  }, TOAST_TTL_MS);
}

/** Watch the toast region for server-pushed (htmx OOB) toasts and auto-dismiss them. */
function setupToasts(): void {
  const container = document.getElementById('toasts');
  if (!container) {
    return;
  }

  for (const existing of container.querySelectorAll<HTMLElement>('.toast')) {
    scheduleDismiss(existing);
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement && node.classList.contains('toast')) {
          scheduleDismiss(node);
        }
      }
    }
  });
  observer.observe(container, { childList: true });
}

/**
 * Toggle light/dark from [data-theme-toggle]. The chosen theme is written to the
 * `theme` cookie so the server renders <html data-theme="..."> on the next request
 * (no flash-of-wrong-theme). One year, Lax so it rides top-level navigations.
 */
function setupThemeToggle(): void {
  const CYCLE = ['light', 'dark', 'system'] as const;
  const LABELS: Record<string, string> = { light: 'Light', dark: 'Dark', system: 'System' };

  function applyTheme(theme: string): void {
    const root = document.documentElement;
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.dataset.theme = prefersDark ? 'dark' : 'light';
    } else {
      root.dataset.theme = theme;
    }
    // biome-ignore lint/suspicious/noDocumentCookie: a plain cookie is intentional — the Go server reads it to render <html data-theme> server-side; the Cookie Store API is async.
    document.cookie = `theme=${theme}; path=/; max-age=31536000; samesite=lax`;
    const label = document.querySelector('[data-theme-label]');
    if (label) label.textContent = LABELS[theme] ?? theme;
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-theme-toggle]')) {
      return;
    }
    const root = document.documentElement;
    const current = document.cookie.match(/theme=(\w+)/)?.[1] ?? root.dataset.theme ?? 'light';
    const idx = CYCLE.indexOf(current as (typeof CYCLE)[number]);
    const next = CYCLE[(idx + 1) % CYCLE.length] ?? 'light';
    applyTheme(next);
  });
}

/**
 * Replace the server-rendered UTC fallback in <time data-local-time> elements with the
 * instant in the viewer's own timezone and the page's language (timestamps are UTC
 * end to end; presenting them locally is the client's job).
 */
function localizeTimes(): void {
  const format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  for (const el of document.querySelectorAll<HTMLTimeElement>('time[data-local-time]')) {
    const instant = new Date(el.dateTime);
    if (!Number.isNaN(instant.getTime())) {
      el.textContent = format.format(instant);
    }
  }
}

function init(): void {
  setupDialogs();
  setupToasts();
  setupThemeToggle();
  localizeTimes();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
